import { Subject } from 'rxjs';
import { UUID } from '../models/classes/utilities';
import {
    IKB3DMessage,
    IKB3DStartTaskMessage,
    IKB3DCompleteTaskMessage,
    TaskStartPayloads,
    eWorkerTasks,
    eWorkerSendData,
    SendWorkerDataPayload,
    IKB3DTransferMessage,
} from '@kb3d/models/interfaces/worker-types';

const THREAD_COUNT = 2;

export class Kb3dWorker {
    workerPool: Worker[] = [];
    toggle = 0;
    requestsInFlight = new Map<string, Subject<IKB3DCompleteTaskMessage<any>>>();

    public static workerPath = '';

    constructor() {
        if (!Kb3dWorker.workerPath) {
            throw Error('Worker Script path not set');
        }
        let workerPath = Kb3dWorker.workerPath;

        if (workerPath.startsWith('/')) {
            workerPath = document.location.origin + workerPath;
        }
        const content = `importScripts( "${workerPath}" );`;
        const workerBlobUrl = URL.createObjectURL(new Blob([content], { type: 'text/javascript' }));

        const workerReturnMessage = (event: MessageEvent<IKB3DMessage | IKB3DCompleteTaskMessage<eWorkerTasks>>) => {
            if ('task' in event.data) {
                const data = event.data;
                const sub = this.requestsInFlight.get(data.taskId);

                if (sub) {
                    // We have to remove the request from the inflight map first because the sub.next can trigger the dispose
                    // But the sub.complete has to be after the sub.next
                    if (data.type === 'taskComplete') {
                        this.requestsInFlight.delete(data.taskId);
                    }

                    if (data.type === 'taskError') {
                        sub.error(data.payload);
                    } else {
                        sub.next(data);
                    }

                    if (data.type === 'taskComplete') {
                        sub.complete();
                    }
                }
            }
        };

        for (let i = 0; i < THREAD_COUNT; i++) {
            const newWorker = new Worker(workerBlobUrl);
            newWorker.onmessage = workerReturnMessage;
            this.workerPool.push(newWorker);
        }
        URL.revokeObjectURL(workerBlobUrl);
    }

    sendData<T extends eWorkerSendData>(dataType: T, payload: SendWorkerDataPayload[T], transfer?: ArrayBuffer[]) {
        const messagePayload: IKB3DTransferMessage<T> = {
            type: 'sendData',
            dataType,
            payload,
        };

        // For sending data, we don't want to transfer the reference because we need to potentially send it to many workers, necessitating a clone
        this.workerPool.forEach(worker => {
            worker.postMessage(messagePayload);
        });
    }

    startTask<T extends eWorkerTasks>(task: T, taskPayload: TaskStartPayloads[T], transfer?: ArrayBuffer[]) {
        const messagePayload: IKB3DStartTaskMessage<T> = {
            type: 'taskStart',
            task,
            taskId: UUID.generate(),
            payload: taskPayload,
        };
        this.workerPool[this.toggle].postMessage(messagePayload, transfer ? { transfer } : undefined);

        const sub = new Subject<IKB3DCompleteTaskMessage<T>>();
        this.requestsInFlight.set(messagePayload.taskId, sub);
        this.toggle = (this.toggle + 1) % THREAD_COUNT;

        return sub;
    }

    dispose() {
        this.workerPool.forEach(worker => {
            worker.terminate();
        });

        this.requestsInFlight.forEach(sub => {
            sub.error('Worker Terminated');
            sub.complete();
        });
        this.requestsInFlight.clear();
    }
}
